import React, { useContext, useEffect, useState } from "react";
import styles from "../../../form.module.css";
import {
  Button,
  Col,
  DatePicker,
  Flex,
  Form,
  Input,
  Row,
  Select,
  Typography,
} from "antd";
import useFetch from "../../../../../hooks/useFetch";
import useToast from "../../../../../hooks/useToast";
import {
  eDocType,
  eFORMSTATUS,
  eFullTimeOrPartTime,
  eResultCode,
  INPUT_LENGTH,
} from "../../../../../utils/enum";
import { ToastOpen, ToastType } from "../../../../../state/toast/slice";
import { CloseOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import Text from "../../../../../context/language-context";
import { Table } from "antd";
import type { TableProps } from "antd";
import { useNavigate, useSearchParams } from "react-router-dom";
import { UserContext } from "../../../../../context";
import {
  GetFormData,
  updateField,
  UploadDocs,
} from "../../../../../utils/api.constant";

import AuthUtil from "../../../../../utils/auth";
import { FaCheck } from "react-icons/fa";
import { TFormProps } from "../../../../../types";
import { LoaderContext } from "../../../../../context/loader";
import {
  SCHOOL_PT2_Q2,
  SCHOOL_PT2_Q3,
  SCHOOL_PT2_Q4,
  SCHOOL_PT2_Q5,
  SCHOOL_PT2_Q6,
  SCHOOL_PT2_Q7,
  SCHOOL_PT2_Q8,
  SCHOOL_PT2_Q9,
  DATA_TYPE_SCHOOL_PT2_Q10 as DATA_TYPE_SCHOOL_PT2_Q16,
} from "../../../../../types/table_types";
import {
  disableFutureYears,
  getNumberInLanguage,
} from "../../../../../utils/helperFunctions";
import { Rule } from "antd/es/form";
import EncryptUtils from "../../../../../utils/encrypt";
import dayjs from "dayjs";
import Utils from "../../../../../utils";
import {
  disableFutureDates_on_input_date_max,
  disableFutureDates_on_input_month_max,
  numberInputRules,
  validateFile,
} from "../../../../../utils/input.validator";

const formItemLayout = {
  labelCol: {
    span: 24,
  },
  wrapperCol: {
    span: 24,
  },
};

const SchoolPartTwo = ({
  disabled,
  currentFormId,
  currentUfmId,
}: TFormProps) => {
  const [form] = Form.useForm();
  const { post } = useFetch();
  const { onShowToast } = useToast();
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const [formId, setFormId] = useState<string | null>(null);
  const [ufmId, setUfmId] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [formFor, setFormFor] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState<Boolean>(false);

  // Loader Context For show Loader
  const contextForLoader = useContext(LoaderContext);
  const { isLoading, setIsLoading } = contextForLoader as any;
  const PREVIEW = Text({ tid: "PREVIEW", def: "Preview" });
  const [applyPdfStyles, setApplyPdfStyles] = useState(false);
  const DROPDOWN_PLACEHOLDER = Text({
    tid: "DROPDOWN_PLACEHOLDER",
    def: "DROPDOWN_PLACEHOLDER",
  });

  const [rowCount, setRowCount] = useState({
    SCHOOL_PT2_Q2: 1,
    SCHOOL_PT2_Q3: 1,
    SCHOOL_PT2_Q4: 1,
    SCHOOL_PT2_Q5: 1,
    SCHOOL_PT2_Q6: 1,
    SCHOOL_PT2_Q7: 1,
    SCHOOL_PT2_Q8: 1,
    SCHOOL_PT2_Q9: 1,
    SCHOOL_PT2_Q16: 1,
  });
  const inputRequiredRule: Rule[] = [
    {
      required: true,
      message: Text({ tid: "REQUIRED", def: "Required" }),
    },
  ];

  const [guruSignature, setGuruSignature] = useState<string | null>(null);

  const [documentsData, setDocumentsData] = useState<any>([]);

  const uploadDocument = async (
    file: File,
    type: eDocType,
    documentName?: string
  ) => {
    try {
      setIsUploading(true);

      const formData = new FormData();
      formData.append("file", file);
      formData.append("docType", type.toString());
      formData.append("ufmId", ufmId || "");
      formData.append("userId", user?.id?.toString() || "");
      formData.append("labelName", documentName || "");
      formData.append("currentStage", eFORMSTATUS.STAGE2INPROGRESS.toString());
      const response = await fetch(
        `${process.env.REACT_APP_PUBLIC_API_BASEURL}${UploadDocs}`,
        {
          method: "POST",
          body: formData,
          headers: { ...AuthUtil.getToken() },
        }
      );
      const res = await response.json();
      const { data, dataResponse } = res;
      const { returnCode, description } = dataResponse;

      if (returnCode == eResultCode.SUCCESS) {
        setIsUploading(false);

        switch (documentName) {
          case "SCHOOL_PT2_Q12":
            setGuruSignature(data.docUrl.toString());
            break;

          default:
            break;
        }
        return data.docUrl.toString();
      } else {
        setIsUploading(false);

        console.error("Failed to upload images", response.statusText);
      }
    } catch (error) {
      console.error(error);
      setIsUploading(false);
    }
  };

  const handleGeneratePDF = (id: string, fileName: string) => {
    setApplyPdfStyles(true);
    setTimeout(() => {
      Utils.generatePDF(id, fileName);
      setApplyPdfStyles(false);
    }, 0);
  };

  const handleFileUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    form: any,
    index: number,
    name: string,
    fieldName: string,
    fileFieldName: string,
    handleFieldChange: (field: string, value: any) => void,
    uploadDocument: (
      file: File,
      type: eDocType,
      documentName?: string
    ) => Promise<string>
  ) => {
    if (e.target.files && e.target.files[0]) {
      // Get current row data
      const currentData = form.getFieldValue(fieldName) || [];
      const currentRow = currentData[index] || {};

      // Validate that the name/title field is filled before upload
      let nameField = "";
      let fieldLabel = "";

      switch (fieldName) {
        case "SCHOOL_PT2_Q2":
          nameField = currentRow.teacherName;
          fieldLabel = "Teacher Name";
          break;
        case "SCHOOL_PT2_Q9":
          nameField = currentRow.awardHonorName;
          fieldLabel = "Award/Honor Name";
          break;
        default:
          break;
      }

      if (!nameField || nameField.trim() === "") {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.leftBottom,
          content: `Please enter the ${fieldLabel} before uploading a document.`,
        });
        // Reset the file input
        e.target.value = "";
        return;
      }

      const docId = await uploadDocument(
        e.target.files[0],
        eDocType.OTHER,
        `${fieldName}_${index + 1}`
      );

      form.setFieldValue(
        [fieldName, parseInt(name), `${fieldName}_DOC`],
        docId
      );

      const updatedJson = JSON.parse(
        JSON.stringify(form.getFieldValue(fieldName))
      ).map((item: any) => ({
        ...item,
        [fileFieldName]: "",
      }));
      try {
        await handleFieldChange(fieldName, serializeTableData(updatedJson));
        await getFormData();

        // Update Q10 with new document list and save it
        const uploadedDocuments = getUploadedDocuments();
        form.setFieldsValue({
          SCHOOL_PT2_Q16: uploadedDocuments,
        });
        await handleFieldChange(
          "SCHOOL_PT2_Q16",
          JSON.stringify(uploadedDocuments)
        );

        onShowToast({
          type: ToastType.success,
          title: <FaCheck />,
          position: ToastOpen.leftBottom,
          content: "File uploaded successfully!",
        });
      } catch (error) {
        console.error("Error during file upload or data refresh:", error);

        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.leftBottom,
          content: "Failed to upload file. Please try again.",
        });
      }
    }
  };

  // Function to collect all uploaded document titles
  const getUploadedDocuments = () => {
    const documents: { documentDetails: string; documentUrl?: string }[] = [];

    // Collect from Q2
    const q2Data = form.getFieldValue("SCHOOL_PT2_Q2") || [];
    q2Data.forEach((item: any, index: number) => {
      const docUrl = form.getFieldValue([
        "SCHOOL_PT2_Q2",
        index,
        "SCHOOL_PT2_Q2_DOC",
      ]);
      if (docUrl && item.teacherName) {
        documents.push({
          documentDetails: `${item.teacherName}`,
          documentUrl: docUrl,
        });
      }
    });

    // Collect from Q9
    const q9Data = form.getFieldValue("SCHOOL_PT2_Q9") || [];
    q9Data.forEach((item: any, index: number) => {
      const docUrl = form.getFieldValue([
        "SCHOOL_PT2_Q9",
        index,
        "SCHOOL_PT2_Q9_DOC",
      ]);
      if (docUrl && item.awardHonorName) {
        documents.push({
          documentDetails: `${item.awardHonorName}`,
          documentUrl: docUrl,
        });
      }
    });

    return documents;
  };

  useEffect(() => {
    const initialData = {
      SCHOOL_PT2_Q2: [
        {
          teacherName: "",
          vedaShakha: "",
          vedicStudyLevel: "",
          age: "",
          fullTimeOrPartTime: "",
          SCHOOL_PT2_Q2_DOC: "",
        },
      ],
      SCHOOL_PT2_Q3: [
        {
          teacherName: "",
          vedaShakha: "",
          startingOfTeaching: "",
          endingOfTeaching: "",
          reasonOfLeaving: "",
        },
      ],
      SCHOOL_PT2_Q4: [
        {
          teacherName: "",
          awardName: "",
          instituteName: "",
          year: "",
        },
      ],
      SCHOOL_PT2_Q5: [
        {
          vedicStudentName: "",
          age: "",
          vedaBranch: "",
          studyLevel: "",
        },
      ],
      SCHOOL_PT2_Q6: [
        {
          vedicStudentName: "",
          vedicStudyLevel: "",
          examinationInstitutionName: "",
          year: "",
          category: "",
        },
      ],
      SCHOOL_PT2_Q7: [
        {
          vedicStudentName: "",
          awardName: "",
          institutionName: "",
          year: "",
        },
      ],
      SCHOOL_PT2_Q8: [
        {
          formarStudentName: "",
          year: "",
          vedicBranch: "",
          currentInstitutionOfVedicTeaching: "",
        },
      ],
      SCHOOL_PT2_Q9: [
        {
          awardHonorName: "",
          institutionName: "",
          year: "",
          SCHOOL_PT2_Q9_DOC: "",
        },
      ],
      SCHOOL_PT2_Q16: [
        {
          documentDetails: "",
        },
      ],
    };

    form.setFieldsValue(initialData);
  }, []);

  const handleFieldChange = async (
    field: string,
    value: any,
    bulkPayload?: any,
    isSubmitted?: boolean
  ) => {
    if (formId && ufmId) {
      try {
        const payload: any = {
          data: {
            id: formId,
            userFormMappingId: ufmId,
          },
        };
        if (field.length > 0) {
          const trimmedValue = value.trim();
          if (trimmedValue.length) {
            payload.data[field] = trimmedValue || "";
          } else {
            form.setFieldValue(field, "");
            return;
          }
        }
        if (bulkPayload) {
          payload.data = {
            ...payload.data,
            ...bulkPayload,
          };
        }
        if (isSubmitted) {
          setIsSubmitting(true);

          payload.data.isSubmitted = true;
        }
        const response = await post(updateField, payload);
        const { dataResponse } = response;
        const { returnCode, description } = dataResponse;
        if (returnCode == eResultCode.SUCCESS) {
          if (isSubmitted) {
            onShowToast({
              type: ToastType.success,
              title: <FaCheck />,
              position: ToastOpen.leftBottom,
              content: "Form Submitted Successfully!",
            });
            setIsSubmitting(false);
            setTimeout(() => {
              navigate("/");
            }, 1000);
          }
        } else if (returnCode == eResultCode.INVALID_REQUEST) {
          onShowToast({
            type: ToastType.error,
            title: <ExclamationCircleOutlined />,
            position: ToastOpen.leftBottom,
            content: description,
          });
        }
      } catch (error) {
        console.error("Student Form 1 Error : ", error);
      }
    }
  };

  const handleDateChange = (
    form: any,
    fieldName: string,
    index: number,
    dateString: string,
    handleFieldChange: (field: string, value: any) => void
  ) => {
    if (typeof dateString === "string" && dateString.length === 4) {
      handleFieldChange(
        fieldName,
        JSON.stringify(
          form.getFieldValue(fieldName).map((item: any, i: number) =>
            i === index
              ? {
                  ...item,
                  year: dateString,
                }
              : {
                  ...item,
                  year:
                    typeof item.year === "string" && item.year.length === 4
                      ? item.year // Keep existing year as string if it's already in YYYY format
                      : (dayjs(item.year, "YYYY") as any)["$y"], // Only convert if it's not already a YYYY string
                }
          )
        )
      );
    }
  };

  // Helper function to properly serialize table data with year fields
  const serializeTableData = (tableData: any[]) => {
    return JSON.stringify(
      tableData.map((item: any) => {
        const serializedItem = { ...item };
        // Convert year field to string if it exists and is not already a string
        if (serializedItem.year) {
          if (typeof serializedItem.year === "string") {
            // Check if it's already in YYYY format
            if (/^\d{4}$/.test(serializedItem.year)) {
              // If it's already a string in YYYY format, keep it as is
              serializedItem.year = serializedItem.year;
            } else {
              // If it's a string but not in YYYY format, convert it
              serializedItem.year = dayjs(serializedItem.year)?.format("YYYY");
            }
          } else if (
            serializedItem.year._isAMomentObject ||
            serializedItem.year.$isDayjsObject
          ) {
            // If it's a dayjs/moment object, format it as YYYY
            serializedItem.year = dayjs(serializedItem.year).format("YYYY");
          } else if (serializedItem.year instanceof Date) {
            // If it's a Date object, format it as YYYY
            serializedItem.year = dayjs(serializedItem.year).format("YYYY");
          } else {
            // For any other type, try to convert it
            serializedItem.year = dayjs(serializedItem.year).format("YYYY");
          }
        }
        return serializedItem;
      })
    );
  };

  // Generic function to remove a row
  const removeRow = async (form: any, key: string, index: number) => {
    const currentData = form.getFieldValue(key) || [];
    const updatedData = currentData.filter((_: any, i: number) => i !== index);
    form.setFieldsValue({
      [key]: updatedData,
    });
    setRowCount((prev) => ({ ...prev, [key]: updatedData.length }));
    await handleFieldChange(key, serializeTableData(updatedData));

    // Update Q10 summary table if the removed row was from a table with documents
    const tablesWithDocuments = ["SCHOOL_PT2_Q2", "SCHOOL_PT2_Q9"];
    if (tablesWithDocuments.includes(key)) {
      // Small delay to ensure form state is updated
      setTimeout(async () => {
        const uploadedDocuments = getUploadedDocuments();
        form.setFieldsValue({
          SCHOOL_PT2_Q16: uploadedDocuments,
        });
        await handleFieldChange(
          "SCHOOL_PT2_Q16",
          JSON.stringify(uploadedDocuments)
        );
      }, 100);
    }
  };

  const getFormData = async () => {
    setIsLoading(true);
    try {
      const payload: any = {
        data: {
          id: formId,
          userFormMappingId: ufmId,
          isPtOne: false,
          year: 2025,
        },
      };

      const response = await post(GetFormData, payload);
      const { dataResponse, data } = response;
      const { returnCode } = dataResponse;
      if (returnCode == eResultCode.SUCCESS) {
        const formData = Object.values(data?.schoolPt2Forms || {}).every(
          (value) => value === null
        )
          ? {}
          : data?.schoolPt2Forms || {};

        // List of fields to exclude (file input fields)
        const jsonStringFields = [
          "SCHOOL_PT2_Q2",
          "SCHOOL_PT2_Q3",
          "SCHOOL_PT2_Q4",
          "SCHOOL_PT2_Q5",
          "SCHOOL_PT2_Q6",
          "SCHOOL_PT2_Q7",
          "SCHOOL_PT2_Q8",
          "SCHOOL_PT2_Q9",
          "SCHOOL_PT2_Q16",
        ];

        const fileFields = ["SCHOOL_PT2_Q12"];

        const JSONwithYearFields = [
          "SCHOOL_PT2_Q4",
          "SCHOOL_PT2_Q6",
          "SCHOOL_PT2_Q7",
          "SCHOOL_PT2_Q8",
          "SCHOOL_PT2_Q9",
        ];

        const formattedData = Object.keys(formData).reduce(
          (acc: { [key: string]: any }, key) => {
            if (jsonStringFields.includes(key)) {
              try {
                const parsedArr = formData[key]
                  ? JSON.parse(formData[key])
                  : [{}];

                if (JSONwithYearFields.includes(key)) {
                  parsedArr.forEach((item: any) => {
                    item.year = dayjs(item.year.toString(), "YYYY")?.isValid()
                      ? dayjs(item.year.toString(), "YYYY")
                      : item.year;
                  });
                }
                acc[key] = parsedArr;
              } catch (error) {
                console.error(error);
              }
            } else if (fileFields.includes(key)) {
              acc[key] = "";
            } else {
              acc[key] = formData[key];
            }
            return acc;
          },
          {}
        );

        if (formattedData) {
          form.setFieldsValue(formattedData);
        }

        setDocumentsData(data?.documents);

        setIsLoading(false);
      }
    } catch (error) {
      console.error(error);
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (formId?.length && ufmId?.length) {
      getFormData();
    }
  }, [formId, ufmId]);

  useEffect(() => {
    const documentFields = [{ id: guruSignature, field: "SCHOOL_PT2_Q12" }];

    documentFields.forEach(({ id, field }) => {
      if (id) {
        handleFieldChange(field, id);
      }
    });
  }, [guruSignature]);

  // Auto-update Q10 display when component loads (without backend save)
  useEffect(() => {
    const uploadedDocuments = getUploadedDocuments();
    form.setFieldsValue({
      SCHOOL_PT2_Q16: uploadedDocuments,
    });
  }, [formId, ufmId]); // Only run when form data is loaded

  // Q2 -----------------------------------------Q2-TABLE-COLUMNS-AND-DATA-SOURCE-----------------------------------------
  const SCHOOL_PT2_Q2: TableProps<SCHOOL_PT2_Q2>["columns"] = [
    {
      title: Text({ tid: "SERIAL_NUMBER", def: "SERIAL_NUMBER" }),
      dataIndex: "serialNumber",
      key: "serialNumber",
      width: "100px",
      render: (_, record, index) => getNumberInLanguage(index + 1),
    },
    {
      title: Text({ tid: "SCHOOL_PT2_Q2_A", def: "Teacher's name" }),
      dataIndex: "teacherName",
      key: "teacherName",
      render: (_, record, index) => {
        const docUrl = form.getFieldValue([
          "SCHOOL_PT2_Q2",
          index,
          "SCHOOL_PT2_Q2_DOC",
        ]);
        return (
          <Form.Item
            name={["SCHOOL_PT2_Q2", index, "teacherName"]}
            rules={inputRequiredRule}
          >
            <Input
              disabled={disabled || !!docUrl}
              onBlur={(e) =>
                handleFieldChange(
                  "SCHOOL_PT2_Q2",
                  JSON.stringify(form.getFieldValue("SCHOOL_PT2_Q2"))
                )
              }
            />
          </Form.Item>
        );
      },
    },
    {
      title: Text({ tid: "SCHOOL_PT2_Q2_B", def: "Veda Shakha" }),
      dataIndex: "vedaShakha",
      key: "vedaShakha",
      render: (_, record, index) => (
        <Form.Item
          name={["SCHOOL_PT2_Q2", index, "vedaShakha"]}
          rules={inputRequiredRule}
        >
          <Input
            disabled={disabled}
            onBlur={(e) =>
              handleFieldChange(
                "SCHOOL_PT2_Q2",
                JSON.stringify(form.getFieldValue("SCHOOL_PT2_Q2"))
              )
            }
          />
        </Form.Item>
      ),
    },
    {
      title: Text({ tid: "SCHOOL_PT2_Q2_C", def: "Ved study level" }),
      dataIndex: "vedicStudyLevel",
      key: "vedicStudyLevel",
      render: (_, record, index) => (
        <Form.Item
          name={["SCHOOL_PT2_Q2", index, "vedicStudyLevel"]}
          rules={inputRequiredRule}
        >
          <Input
            disabled={disabled}
            onBlur={(e) =>
              handleFieldChange(
                "SCHOOL_PT2_Q2",
                JSON.stringify(form.getFieldValue("SCHOOL_PT2_Q2"))
              )
            }
          />
        </Form.Item>
      ),
    },
    {
      title: Text({
        tid: "SCHOOL_PT2_Q2_D",
        def: "Age",
      }),
      dataIndex: "age",
      key: "age",
      render: (_, record, index) => (
        <Form.Item
          name={["SCHOOL_PT2_Q2", index, "age"]}
          rules={numberInputRules({
            required: true,
            allowNegative: false,
            min: 0,
            max: 120,
          })}
        >
          <Input
            type='number'
            disabled={disabled}
            onBlur={(e) =>
              handleFieldChange(
                "SCHOOL_PT2_Q2",
                JSON.stringify(form.getFieldValue("SCHOOL_PT2_Q2"))
              )
            }
          />
        </Form.Item>
      ),
    },
    {
      title: Text({ tid: "SCHOOL_PT2_Q2_E", def: "Full-time / part-time" }),
      dataIndex: "fullTimeOrPartTime",
      key: "fullTimeOrPartTime",
      render: (_, record, index) => (
        <Form.Item
          name={["SCHOOL_PT2_Q2", index, "fullTimeOrPartTime"]}
          rules={inputRequiredRule}
        >
          <Select
            disabled={disabled}
            onChange={(value) => {
              handleFieldChange(
                "SCHOOL_PT2_Q2",
                JSON.stringify(form.getFieldValue("SCHOOL_PT2_Q2"))
              );
            }}
            placeholder={DROPDOWN_PLACEHOLDER}
          >
            <Select.Option value={eFullTimeOrPartTime.FULLTIME}>
              {Text({ tid: "FULLTIME", def: "FULLTIME" })}
            </Select.Option>
            <Select.Option value={eFullTimeOrPartTime.PARTTIME}>
              {Text({ tid: "PARTTIME", def: "PARTTIME" })}
            </Select.Option>
          </Select>
        </Form.Item>
      ),
      width: "200px",
    },
    {
      title: Text({ tid: "ATTACHED_DOCUMENT", def: "Document Upload" }),
      dataIndex: "documentUpload",
      key: "documentUpload",
      width: "20%",
      render: (_: any, record: any, index: number) => {
        const docUrl = form.getFieldValue([
          "SCHOOL_PT2_Q2",
          index,
          "SCHOOL_PT2_Q2_DOC",
        ]);
        return (
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <input
              type='file'
              accept='.pdf,.doc,.docx,.jpg,.jpeg,.png'
              disabled={disabled}
              onChange={(e) =>
                handleFileUpload(
                  e,
                  form,
                  index,
                  index.toString(),
                  "SCHOOL_PT2_Q2",
                  "documentUpload",
                  handleFieldChange,
                  uploadDocument
                )
              }
              style={{ fontSize: "12px", cursor: "pointer" }}
            />
            {docUrl && (
              <a
                href={`${process.env.REACT_APP_PUBLIC_API_MEDIA_BASEURL}${docUrl}`}
                target='_blank'
                rel='noopener noreferrer'
                className='primary-link'
              >
                {Text({ tid: "PREVIEW", def: "Preview" })}
              </a>
            )}
          </div>
        );
      },
    },
    {
      title: Text({ tid: "ACTION", def: "ACTION" }),
      dataIndex: "action",
      width: "100px",

      key: "action",
      render: (_, record, index) => (
        <Form.Item>
          <Button
            disabled={disabled}
            variant='filled'
            onClick={() => removeRow(form, "SCHOOL_PT2_Q2", index)}
            className='actionButton'
          >
            {Text({ tid: "REMOVE", def: "remove" })}
          </Button>
        </Form.Item>
      ),
    },
  ];

  // Q3 -----------------------------------------Q3-TABLE-COLUMNS-AND-DATA-SOURCE-----------------------------------------
  const SCHOOL_PT2_Q3: TableProps<SCHOOL_PT2_Q3>["columns"] = [
    {
      title: Text({ tid: "SERIAL_NUMBER", def: "SERIAL_NUMBER" }),
      dataIndex: "serialNumber",
      key: "serialNumber",
      width: "100px",
      render: (_, record, index) => getNumberInLanguage(index + 1),
    },
    {
      title: Text({ tid: "SCHOOL_PT2_Q3_A", def: "Teacher's name" }),
      dataIndex: "teacherName",
      key: "teacherName",
      render: (_, record, index) => (
        <Form.Item
          name={["SCHOOL_PT2_Q3", index, "teacherName"]}
          rules={inputRequiredRule}
        >
          <Input
            disabled={disabled}
            onBlur={(e) =>
              handleFieldChange(
                "SCHOOL_PT2_Q3",
                JSON.stringify(form.getFieldValue("SCHOOL_PT2_Q3"))
              )
            }
          />
        </Form.Item>
      ),
    },
    {
      title: Text({ tid: "SCHOOL_PT2_Q3_B", def: "Veda Shakha" }),
      dataIndex: "vedaShakha",
      key: "vedaShakha",
      render: (_, record, index) => (
        <Form.Item
          name={["SCHOOL_PT2_Q3", index, "vedaShakha"]}
          rules={inputRequiredRule}
        >
          <Input
            disabled={disabled}
            onBlur={(e) =>
              handleFieldChange(
                "SCHOOL_PT2_Q3",
                JSON.stringify(form.getFieldValue("SCHOOL_PT2_Q3"))
              )
            }
          />
        </Form.Item>
      ),
    },
    {
      title: Text({ tid: "TEACHING_DURATION", def: "TEACHING_DURATION" }),
      children: [
        {
          title: Text({ tid: "SCHOOL_PT2_Q3_C", def: "Starting of Teaching" }),
          dataIndex: "startingOfTeaching",
          key: "startingOfTeaching",
          width: "200px",
          render: (_, record, index) => (
            <Form.Item
              name={["SCHOOL_PT2_Q3", index, "startingOfTeaching"]}
              rules={inputRequiredRule}
            >
              <Input
                disabled={disabled}
                type='month'
                max={disableFutureDates_on_input_month_max()}
                onClick={(e) => e.currentTarget.showPicker()}
                onBlur={(e) =>
                  handleFieldChange(
                    "SCHOOL_PT2_Q3",
                    JSON.stringify(form.getFieldValue("SCHOOL_PT2_Q3"))
                  )
                }
              />
            </Form.Item>
          ),
        },
        {
          title: Text({ tid: "SCHOOL_PT2_Q3_D", def: "Ending of Teaching" }),
          dataIndex: "endingOfTeaching",
          key: "endingOfTeaching",
          width: "200px",
          render: (_, record, index) => (
            <Form.Item
              name={["SCHOOL_PT2_Q3", index, "endingOfTeaching"]}
              rules={inputRequiredRule}
            >
              <Input
                disabled={disabled}
                type='month'
                max={disableFutureDates_on_input_month_max()}
                onClick={(e) => e.currentTarget.showPicker()}
                onBlur={(e) =>
                  handleFieldChange(
                    "SCHOOL_PT2_Q3",
                    JSON.stringify(form.getFieldValue("SCHOOL_PT2_Q3"))
                  )
                }
              />
            </Form.Item>
          ),
        },
      ],
    },
    {
      title: Text({ tid: "SCHOOL_PT2_Q3_E", def: "Reason of Leaving" }),
      dataIndex: "reasonOfLeaving",
      key: "reasonOfLeaving",
      render: (_, record, index) => (
        <Form.Item
          name={["SCHOOL_PT2_Q3", index, "reasonOfLeaving"]}
          rules={inputRequiredRule}
        >
          <Input
            disabled={disabled}
            onBlur={(e) =>
              handleFieldChange(
                "SCHOOL_PT2_Q3",
                JSON.stringify(form.getFieldValue("SCHOOL_PT2_Q3"))
              )
            }
          />
        </Form.Item>
      ),
    },
    {
      title: Text({ tid: "ACTION", def: "ACTION" }),
      dataIndex: "action",
      width: "100px",

      key: "action",
      render: (_, record, index) => (
        <Form.Item>
          <Button
            disabled={disabled}
            variant='filled'
            onClick={() => removeRow(form, "SCHOOL_PT2_Q3", index)}
            className='actionButton'
          >
            {Text({ tid: "REMOVE", def: "remove" })}
          </Button>
        </Form.Item>
      ),
    },
  ];

  // Q4 -----------------------------------------Q4-TABLE-COLUMNS-AND-DATA-SOURCE-----------------------------------------
  const SCHOOL_PT2_Q4: TableProps<SCHOOL_PT2_Q4>["columns"] = [
    {
      title: Text({ tid: "SERIAL_NUMBER", def: "SERIAL_NUMBER" }),
      dataIndex: "serialNumber",
      key: "serialNumber",
      width: "100px",
      render: (_, record, index) => getNumberInLanguage(index + 1),
    },
    {
      title: Text({ tid: "SCHOOL_PT2_Q4_A", def: "Teacher's name" }),
      dataIndex: "teacherName",
      key: "teacherName",
      render: (_, record, index) => (
        <Form.Item
          name={["SCHOOL_PT2_Q4", index, "teacherName"]}
          rules={inputRequiredRule}
        >
          <Input
            disabled={disabled}
            onBlur={(e) =>
              handleFieldChange(
                "SCHOOL_PT2_Q4",
                serializeTableData(form.getFieldValue("SCHOOL_PT2_Q4"))
              )
            }
          />
        </Form.Item>
      ),
    },
    {
      title: Text({ tid: "SCHOOL_PT2_Q4_B", def: "Award Name" }),
      dataIndex: "awardName",
      key: "awardName",
      render: (_, record, index) => (
        <Form.Item
          name={["SCHOOL_PT2_Q4", index, "awardName"]}
          rules={inputRequiredRule}
        >
          <Input
            disabled={disabled}
            onBlur={(e) =>
              handleFieldChange(
                "SCHOOL_PT2_Q4",
                serializeTableData(form.getFieldValue("SCHOOL_PT2_Q4"))
              )
            }
          />
        </Form.Item>
      ),
    },
    {
      title: Text({ tid: "SCHOOL_PT2_Q4_C", def: "Institute Name" }),
      dataIndex: "instituteName",
      key: "instituteName",
      render: (_, record, index) => (
        <Form.Item
          name={["SCHOOL_PT2_Q4", index, "instituteName"]}
          rules={inputRequiredRule}
        >
          <Input
            disabled={disabled}
            onBlur={(e) =>
              handleFieldChange(
                "SCHOOL_PT2_Q4",
                serializeTableData(form.getFieldValue("SCHOOL_PT2_Q4"))
              )
            }
          />
        </Form.Item>
      ),
    },
    {
      title: Text({ tid: "SCHOOL_PT2_Q4_D", def: "Year" }),
      dataIndex: "year",
      key: "year",
      render: (_, record, index) => (
        <Form.Item
          name={["SCHOOL_PT2_Q4", index, "year"]}
          rules={inputRequiredRule}
        >
          <DatePicker
            placeholder='YYYY'
            disabled={disabled}
            disabledDate={disableFutureYears}
            picker='year'
            format='YYYY'
            onKeyDown={(e) => e.preventDefault()}
            onChange={(date, dateString) =>
              handleDateChange(
                form,
                "SCHOOL_PT2_Q4",
                index,
                Array.isArray(dateString) ? dateString[0] : dateString,
                handleFieldChange
              )
            }
          />
        </Form.Item>
      ),
    },
    {
      title: Text({ tid: "ACTION", def: "ACTION" }),
      dataIndex: "action",
      width: "100px",

      key: "action",
      render: (_, record, index) => (
        <Form.Item>
          <Button
            disabled={disabled}
            variant='filled'
            onClick={() => removeRow(form, "SCHOOL_PT2_Q4", index)}
            className='actionButton'
          >
            {Text({ tid: "REMOVE", def: "remove" })}
          </Button>
        </Form.Item>
      ),
    },
  ];

  // Q5 -----------------------------------------Q5-TABLE-COLUMNS-AND-DATA-SOURCE-----------------------------------------
  const SCHOOL_PT2_Q5: TableProps<SCHOOL_PT2_Q5>["columns"] = [
    {
      title: Text({ tid: "SERIAL_NUMBER", def: "SERIAL_NUMBER" }),
      dataIndex: "serialNumber",
      key: "serialNumber",
      width: "100px",
      render: (_, record, index) => getNumberInLanguage(index + 1),
    },
    {
      title: Text({ tid: "SCHOOL_PT2_Q5_A", def: "Ved student's Name" }),
      dataIndex: "vedicStudentName",
      key: "vedicStudentName",
      render: (_, record, index) => (
        <Form.Item
          name={["SCHOOL_PT2_Q5", index, "vedicStudentName"]}
          rules={inputRequiredRule}
        >
          <Input
            disabled={disabled}
            onBlur={(e) =>
              handleFieldChange(
                "SCHOOL_PT2_Q5",
                JSON.stringify(form.getFieldValue("SCHOOL_PT2_Q5"))
              )
            }
          />
        </Form.Item>
      ),
    },
    {
      title: Text({ tid: "SCHOOL_PT2_Q5_B", def: "Age" }),
      dataIndex: "age",
      key: "age",
      render: (_, record, index) => (
        <Form.Item
          name={["SCHOOL_PT2_Q5", index, "age"]}
          rules={numberInputRules({
            required: true,
            allowNegative: false,
            min: 0,
            max: 120,
          })}
        >
          <Input
            type='number'
            disabled={disabled}
            onBlur={(e) =>
              handleFieldChange(
                "SCHOOL_PT2_Q5",
                JSON.stringify(form.getFieldValue("SCHOOL_PT2_Q5"))
              )
            }
          />
        </Form.Item>
      ),
    },
    {
      title: Text({ tid: "SCHOOL_PT2_Q5_C", def: "Veda Branch" }),
      dataIndex: "vedaBranch",
      key: "vedaBranch",
      render: (_, record, index) => (
        <Form.Item
          name={["SCHOOL_PT2_Q5", index, "vedaBranch"]}
          rules={inputRequiredRule}
        >
          <Input
            disabled={disabled}
            onBlur={(e) =>
              handleFieldChange(
                "SCHOOL_PT2_Q5",
                JSON.stringify(form.getFieldValue("SCHOOL_PT2_Q5"))
              )
            }
          />
        </Form.Item>
      ),
    },
    {
      title: Text({ tid: "SCHOOL_PT2_Q5_D", def: "Study level" }),
      dataIndex: "studyLevel",
      key: "studyLevel",
      render: (_, record, index) => (
        <Form.Item
          name={["SCHOOL_PT2_Q5", index, "studyLevel"]}
          rules={inputRequiredRule}
        >
          <Input
            disabled={disabled}
            onBlur={(e) =>
              handleFieldChange(
                "SCHOOL_PT2_Q5",
                JSON.stringify(form.getFieldValue("SCHOOL_PT2_Q5"))
              )
            }
          />
        </Form.Item>
      ),
    },
    {
      title: Text({ tid: "ACTION", def: "ACTION" }),
      dataIndex: "action",
      width: "100px",

      key: "action",
      render: (_, record, index) => (
        <Form.Item>
          <Button
            disabled={disabled}
            variant='filled'
            onClick={() => removeRow(form, "SCHOOL_PT2_Q5", index)}
            className='actionButton'
          >
            {Text({ tid: "REMOVE", def: "remove" })}
          </Button>
        </Form.Item>
      ),
    },
  ];

  // Q6 -----------------------------------------Q6-TABLE-COLUMNS-AND-DATA-SOURCE-----------------------------------------
  const SCHOOL_PT2_Q6: TableProps<SCHOOL_PT2_Q6>["columns"] = [
    {
      title: Text({ tid: "SERIAL_NUMBER", def: "SERIAL_NUMBER" }),
      dataIndex: "serialNumber",
      key: "serialNumber",
      width: "100px",
      render: (_, record, index) => getNumberInLanguage(index + 1),
    },
    {
      title: Text({ tid: "SCHOOL_PT2_Q6_A", def: "Ved student's Name" }),
      dataIndex: "vedicStudentName",
      key: "vedicStudentName",
      render: (_, record, index) => (
        <Form.Item
          name={["SCHOOL_PT2_Q6", index, "vedicStudentName"]}
          rules={inputRequiredRule}
        >
          <Input
            disabled={disabled}
            onBlur={(e) =>
              handleFieldChange(
                "SCHOOL_PT2_Q6",
                serializeTableData(form.getFieldValue("SCHOOL_PT2_Q6"))
              )
            }
          />
        </Form.Item>
      ),
    },
    {
      title: Text({ tid: "SCHOOL_PT2_Q6_B", def: "Ved study level" }),
      dataIndex: "vedicStudyLevel",
      key: "vedicStudyLevel",
      render: (_, record, index) => (
        <Form.Item
          name={["SCHOOL_PT2_Q6", index, "vedicStudyLevel"]}
          rules={inputRequiredRule}
        >
          <Input
            disabled={disabled}
            onBlur={(e) =>
              handleFieldChange(
                "SCHOOL_PT2_Q6",
                serializeTableData(form.getFieldValue("SCHOOL_PT2_Q6"))
              )
            }
          />
        </Form.Item>
      ),
    },
    {
      title: Text({
        tid: "SCHOOL_PT2_Q6_C",
        def: "Examination Institution Name",
      }),
      dataIndex: "examinationInstitutionName",
      key: "examinationInstitutionName",
      render: (_, record, index) => (
        <Form.Item
          name={["SCHOOL_PT2_Q6", index, "examinationInstitutionName"]}
          rules={inputRequiredRule}
        >
          <Input
            disabled={disabled}
            onBlur={(e) =>
              handleFieldChange(
                "SCHOOL_PT2_Q6",
                serializeTableData(form.getFieldValue("SCHOOL_PT2_Q6"))
              )
            }
          />
        </Form.Item>
      ),
    },
    {
      title: Text({ tid: "SCHOOL_PT2_Q6_D", def: "Year" }),
      dataIndex: "year",
      key: "year",
      render: (_, record, index) => (
        <Form.Item
          name={["SCHOOL_PT2_Q6", index, "year"]}
          rules={inputRequiredRule}
        >
          <DatePicker
            placeholder='YYYY'
            disabled={disabled}
            disabledDate={disableFutureYears}
            picker='year'
            onKeyDown={(e) => e.preventDefault()}
            format='YYYY'
            onChange={(date, dateString) =>
              handleDateChange(
                form,
                "SCHOOL_PT2_Q6",
                index,
                Array.isArray(dateString) ? dateString[0] : dateString,
                handleFieldChange
              )
            }
          />
        </Form.Item>
      ),
    },
    {
      title: Text({ tid: "SCHOOL_PT2_Q6_E", def: "Category" }),
      dataIndex: "category",
      key: "category",
      render: (_, record, index) => (
        <Form.Item
          name={["SCHOOL_PT2_Q6", index, "category"]}
          rules={inputRequiredRule}
        >
          <Input
            disabled={disabled}
            onBlur={(e) =>
              handleFieldChange(
                "SCHOOL_PT2_Q6",
                serializeTableData(form.getFieldValue("SCHOOL_PT2_Q6"))
              )
            }
          />
        </Form.Item>
      ),
    },
    {
      title: Text({ tid: "ACTION", def: "ACTION" }),
      dataIndex: "action",
      width: "100px",

      key: "action",
      render: (_, record, index) => (
        <Form.Item>
          <Button
            disabled={disabled}
            variant='filled'
            onClick={() => removeRow(form, "SCHOOL_PT2_Q6", index)}
            className='actionButton'
          >
            {Text({ tid: "REMOVE", def: "remove" })}
          </Button>
        </Form.Item>
      ),
    },
  ];

  // Q7 -----------------------------------------Q7-TABLE-COLUMNS-AND-DATA-SOURCE-----------------------------------------
  const SCHOOL_PT2_Q7: TableProps<SCHOOL_PT2_Q7>["columns"] = [
    {
      title: Text({ tid: "SERIAL_NUMBER", def: "SERIAL_NUMBER" }),
      dataIndex: "serialNumber",
      key: "serialNumber",
      width: "100px",
      render: (_, record, index) => getNumberInLanguage(index + 1),
    },
    {
      title: Text({ tid: "SCHOOL_PT2_Q7_A", def: "Ved student's Name" }),
      dataIndex: "vedicStudentName",
      key: "vedicStudentName",
      render: (_, record, index) => (
        <Form.Item
          name={["SCHOOL_PT2_Q7", index, "vedicStudentName"]}
          rules={inputRequiredRule}
        >
          <Input
            disabled={disabled}
            onBlur={() =>
              handleFieldChange(
                "SCHOOL_PT2_Q7",
                serializeTableData(form.getFieldValue("SCHOOL_PT2_Q7"))
              )
            }
          />
        </Form.Item>
      ),
    },
    {
      title: Text({ tid: "SCHOOL_PT2_Q7_B", def: "Award Name" }),
      dataIndex: "awardName",
      key: "awardName",
      render: (_, record, index) => (
        <Form.Item
          name={["SCHOOL_PT2_Q7", index, "awardName"]}
          rules={inputRequiredRule}
        >
          <Input
            disabled={disabled}
            onBlur={() =>
              handleFieldChange(
                "SCHOOL_PT2_Q7",
                serializeTableData(form.getFieldValue("SCHOOL_PT2_Q7"))
              )
            }
          />
        </Form.Item>
      ),
    },
    {
      title: Text({ tid: "SCHOOL_PT2_Q7_C", def: "Institution Name" }),
      dataIndex: "institutionName",
      key: "institutionName",
      render: (_, record, index) => (
        <Form.Item
          name={["SCHOOL_PT2_Q7", index, "institutionName"]}
          rules={inputRequiredRule}
        >
          <Input
            disabled={disabled}
            onBlur={() =>
              handleFieldChange(
                "SCHOOL_PT2_Q7",
                serializeTableData(form.getFieldValue("SCHOOL_PT2_Q7"))
              )
            }
          />
        </Form.Item>
      ),
    },
    {
      title: Text({ tid: "SCHOOL_PT2_Q7_D", def: "Year" }),
      dataIndex: "year",
      key: "year",
      render: (_, record, index) => (
        <Form.Item
          name={["SCHOOL_PT2_Q7", index, "year"]}
          rules={inputRequiredRule}
        >
          <DatePicker
            placeholder='YYYY'
            disabled={disabled}
            disabledDate={disableFutureYears}
            picker='year'
            onKeyDown={(e) => e.preventDefault()}
            format='YYYY'
            onChange={(date, dateString) =>
              handleDateChange(
                form,
                "SCHOOL_PT2_Q7",
                index,
                Array.isArray(dateString) ? dateString[0] : dateString,
                handleFieldChange
              )
            }
          />
        </Form.Item>
      ),
    },
    {
      title: Text({ tid: "ACTION", def: "ACTION" }),
      dataIndex: "action",
      width: "100px",

      key: "action",
      render: (_, record, index) => (
        <Form.Item>
          <Button
            disabled={disabled}
            variant='filled'
            onClick={() => removeRow(form, "SCHOOL_PT2_Q7", index)}
            className='actionButton'
          >
            {Text({ tid: "REMOVE", def: "remove" })}
          </Button>
        </Form.Item>
      ),
    },
  ];

  // Q8 -----------------------------------------Q8-TABLE-COLUMNS-AND-DATA-SOURCE-----------------------------------------
  const SCHOOL_PT2_Q8: TableProps<SCHOOL_PT2_Q8>["columns"] = [
    {
      title: Text({ tid: "SERIAL_NUMBER", def: "SERIAL_NUMBER" }),
      dataIndex: "serialNumber",
      key: "serialNumber",
      width: "100px",
      render: (_, record, index) => getNumberInLanguage(index + 1),
    },
    {
      title: Text({ tid: "SCHOOL_PT2_Q8_A", def: "Former student's Name" }),
      dataIndex: "formarStudentName",
      key: "formarStudentName",
      render: (_, record, index) => (
        <Form.Item
          name={["SCHOOL_PT2_Q8", index, "formarStudentName"]}
          rules={inputRequiredRule}
        >
          <Input
            disabled={disabled}
            onBlur={() =>
              handleFieldChange(
                "SCHOOL_PT2_Q8",
                serializeTableData(form.getFieldValue("SCHOOL_PT2_Q8"))
              )
            }
          />
        </Form.Item>
      ),
    },
    {
      title: Text({
        tid: "SCHOOL_PT2_Q8_B",
        def: "Year of Ved Study Completion",
      }),
      dataIndex: "year",
      key: "year",
      render: (_, record, index) => (
        <>
          {/* <Form.Item
            name={["SCHOOL_PT2_Q8", index, "year"]}
            rules={inputRequiredRule}
          >
            <Input
              disabled={disabled}
              onBlur={() =>
                handleFieldChange(
                  "SCHOOL_PT2_Q8",
                  JSON.stringify(form.getFieldValue("SCHOOL_PT2_Q8"))
                )
              }
            />
          </Form.Item> */}
          <Form.Item
            name={["SCHOOL_PT2_Q8", index, "year"]}
            rules={inputRequiredRule}
          >
            <DatePicker
              placeholder='YYYY'
              disabled={disabled}
              disabledDate={disableFutureYears}
              picker='year'
              onKeyDown={(e) => e.preventDefault()}
              format='YYYY'
              onChange={(date, dateString) =>
                handleDateChange(
                  form,
                  "SCHOOL_PT2_Q8",
                  index,
                  Array.isArray(dateString) ? dateString[0] : dateString,
                  handleFieldChange
                )
              }
            />
          </Form.Item>
        </>
      ),
    },
    {
      title: Text({ tid: "SCHOOL_PT2_Q8_C", def: "Ved Branch" }),
      dataIndex: "vedicBranch",
      key: "vedicBranch",
      render: (_, record, index) => (
        <Form.Item
          name={["SCHOOL_PT2_Q8", index, "vedicBranch"]}
          rules={inputRequiredRule}
        >
          <Input
            disabled={disabled}
            onBlur={() =>
              handleFieldChange(
                "SCHOOL_PT2_Q8",
                serializeTableData(form.getFieldValue("SCHOOL_PT2_Q8"))
              )
            }
          />
        </Form.Item>
      ),
    },
    {
      title: Text({
        tid: "SCHOOL_PT2_Q8_D",
        def: "Current Institution of Ved Teaching",
      }),
      dataIndex: "currentInstitutionOfVedicTeaching",
      key: "currentInstitutionOfVedicTeaching",
      render: (_, record, index) => (
        <Form.Item
          name={["SCHOOL_PT2_Q8", index, "currentInstitutionOfVedicTeaching"]}
          rules={inputRequiredRule}
        >
          <Input
            disabled={disabled}
            onBlur={() =>
              handleFieldChange(
                "SCHOOL_PT2_Q8",
                serializeTableData(form.getFieldValue("SCHOOL_PT2_Q8"))
              )
            }
          />
        </Form.Item>
      ),
    },
    {
      title: Text({ tid: "ACTION", def: "ACTION" }),
      dataIndex: "action",
      key: "action",
      width: "100px",

      render: (_, record, index) => (
        <Form.Item>
          <Button
            disabled={disabled}
            variant='filled'
            onClick={() => removeRow(form, "SCHOOL_PT2_Q8", index)}
            className='actionButton'
          >
            {Text({ tid: "REMOVE", def: "remove" })}
          </Button>
        </Form.Item>
      ),
    },
  ];

  // Q9 -----------------------------------------Q9-TABLE-COLUMNS-AND-DATA-SOURCE-----------------------------------------
  const SCHOOL_PT2_Q9: TableProps<SCHOOL_PT2_Q9>["columns"] = [
    {
      title: Text({ tid: "SERIAL_NUMBER", def: "SERIAL_NUMBER" }),
      dataIndex: "serialNumber",
      key: "serialNumber",
      width: "100px",
      render: (_, record, index) => getNumberInLanguage(index + 1),
    },
    {
      title: Text({ tid: "SCHOOL_PT2_Q9_A", def: "Award/Honor Name" }),
      dataIndex: "awardHonorName",
      key: "awardHonorName",
      render: (_, record, index) => {
        const docUrl = form.getFieldValue([
          "SCHOOL_PT2_Q9",
          index,
          "SCHOOL_PT2_Q9_DOC",
        ]);
        return (
          <Form.Item
            name={["SCHOOL_PT2_Q9", index, "awardHonorName"]}
            rules={inputRequiredRule}
          >
            <Input
              disabled={disabled || !!docUrl}
              onBlur={() =>
                handleFieldChange(
                  "SCHOOL_PT2_Q9",
                  serializeTableData(form.getFieldValue("SCHOOL_PT2_Q9"))
                )
              }
            />
          </Form.Item>
        );
      },
    },
    {
      title: Text({ tid: "SCHOOL_PT2_Q9_B", def: "Institution Name" }),
      dataIndex: "institutionName",
      key: "institutionName",
      render: (_, record, index) => (
        <Form.Item
          name={["SCHOOL_PT2_Q9", index, "institutionName"]}
          rules={inputRequiredRule}
        >
          <Input
            disabled={disabled}
            onBlur={() =>
              handleFieldChange(
                "SCHOOL_PT2_Q9",
                serializeTableData(form.getFieldValue("SCHOOL_PT2_Q9"))
              )
            }
          />
        </Form.Item>
      ),
    },
    {
      title: Text({ tid: "SCHOOL_PT2_Q9_C", def: "Year" }),
      dataIndex: "year",
      key: "year",
      render: (_, record, index) => (
        <Form.Item
          name={["SCHOOL_PT2_Q9", index, "year"]}
          rules={inputRequiredRule}
        >
          <DatePicker
            placeholder='YYYY'
            disabled={disabled}
            disabledDate={disableFutureYears}
            picker='year'
            format='YYYY'
            onKeyDown={(e) => e.preventDefault()}
            onChange={(date, dateString) =>
              handleDateChange(
                form,
                "SCHOOL_PT2_Q9",
                index,
                Array.isArray(dateString) ? dateString[0] : dateString,
                handleFieldChange
              )
            }
          />
        </Form.Item>
      ),
    },
    {
      title: Text({ tid: "ATTACHED_DOCUMENT", def: "Document Upload" }),
      dataIndex: "documentUpload",
      key: "documentUpload",
      width: "20%",
      render: (_: any, record: any, index: number) => {
        const docUrl = form.getFieldValue([
          "SCHOOL_PT2_Q9",
          index,
          "SCHOOL_PT2_Q9_DOC",
        ]);
        return (
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <input
              type='file'
              accept='.pdf,.doc,.docx,.jpg,.jpeg,.png'
              disabled={disabled}
              onChange={(e) =>
                handleFileUpload(
                  e,
                  form,
                  index,
                  index.toString(),
                  "SCHOOL_PT2_Q9",
                  "documentUpload",
                  handleFieldChange,
                  uploadDocument
                )
              }
              style={{ fontSize: "12px", cursor: "pointer" }}
            />
            {docUrl && (
              <a
                href={`${process.env.REACT_APP_PUBLIC_API_MEDIA_BASEURL}${docUrl}`}
                target='_blank'
                rel='noopener noreferrer'
                className='primary-link'
              >
                {Text({ tid: "PREVIEW", def: "Preview" })}
              </a>
            )}
          </div>
        );
      },
    },
    {
      title: Text({ tid: "ACTION", def: "ACTION" }),
      dataIndex: "action",
      width: "100px",

      key: "action",
      render: (_, record, index) => (
        <Form.Item>
          <Button
            disabled={disabled}
            variant='filled'
            onClick={() => removeRow(form, "SCHOOL_PT2_Q9", index)}
            className='actionButton'
          >
            {Text({ tid: "REMOVE", def: "remove" })}
          </Button>
        </Form.Item>
      ),
    },
  ];

  // Q10 -----------------------------------------Q10-TABLE-COLUMNS-AND-DATA-SOURCE-----------------------------------------

  const TABLE_COLUMNS_SCHOOL_PT2_Q16: TableProps<DATA_TYPE_SCHOOL_PT2_Q16>["columns"] =
    [
      {
        title: Text({ tid: "SERIAL_NUMBER", def: "SERIAL_NUMBER" }),
        dataIndex: "serialNumber",
        key: "serialNumber",
        width: "100px",
        render: (_, record, index) => getNumberInLanguage(index + 1),
      },
      {
        title: Text({ tid: "SCHOOL_PT2_Q16_B", def: "Document details" }),
        dataIndex: "documentDetails",
        key: "documentDetails",
        render: (text: string) => <span style={{ color: "#666" }}>{text}</span>,
      },
      {
        title: Text({ tid: "PREVIEW", def: "Preview" }),
        dataIndex: "preview",
        key: "preview",
        width: "120px",
        render: (_, record: any) => {
          const documentUrl = record.documentUrl;
          return documentUrl ? (
            <a
              href={`${process.env.REACT_APP_PUBLIC_API_MEDIA_BASEURL}${documentUrl}`}
              target='_blank'
              rel='noopener noreferrer'
              className='primary-link'
            >
              {Text({ tid: "PREVIEW", def: "Preview" })}
            </a>
          ) : (
            <span style={{ color: "#ccc" }}>No document</span>
          );
        },
      },
    ];

  // --------------------------------------------------------------------------------

  const LengthCounter: React.FC<{ form: any; fieldName: string }> = ({
    form,
    fieldName,
  }) => {
    // Use useWatch to get real-time updates for the field value
    const fieldValue = Form.useWatch(fieldName, form);

    return (
      <div className='length_counter'>
        {(fieldValue || "").length}/{INPUT_LENGTH.TEXTAREA_LENGTH}
      </div>
    );
  };

  useEffect(() => {
    if (searchParams) {
      const formId = EncryptUtils.decrypt(searchParams.get("id2") || "");
      const ufmId = EncryptUtils.decrypt(searchParams.get("ufmid") || "");
      const formFor = EncryptUtils.decrypt(searchParams.get("ff") || "");
      setFormId(formId);
      setUfmId(ufmId);
      setFormFor(formFor);
    }
    if (currentFormId && currentUfmId) {
      setFormId(EncryptUtils.decrypt(currentFormId));
      setUfmId(EncryptUtils.decrypt(currentUfmId));
    }
  }, [searchParams, currentFormId, currentUfmId]);

  const inputRequiredBasicRule: Rule[] = [
    {
      required: true,
      message: Text({ tid: "REQUIRED", def: "Required" }),
    },
  ];

  return (
    <div
      id='schoolFormPart2'
      className={`${styles.formWrapper} studentTeacherSchoolFormWrapper`}
    >
      <Form
        {...formItemLayout}
        form={form}
        name='register'
        onFinish={() => handleFieldChange("", "", null, true)}
        scrollToFirstError
        layout='vertical'
      >
        {/* PAGE HEADING */}
        <Row>
          <Col>
            <Typography.Title level={3}>
              {Text({
                tid: "SCHOOL_PART_2_FORM_HEADING",
                def: "SCHOOL_PART_2_FORM_HEADING",
              })}
            </Typography.Title>
            {!disabled && (
              <Typography.Text type='danger'>
                {Text({
                  tid: "all_fields_are_mandatory",
                  def: "All fields are mandatory!",
                })}
              </Typography.Text>
            )}
          </Col>
        </Row>
        <Row>
          <Col xs={24}>
            <Typography.Text>
              {Text({
                tid: "SCHOOL_PT1_HEADING_Q1",
                def: "Name and full address of the Ved school:",
              })}
            </Typography.Text>
          </Col>
        </Row>
        {/* Q1). HEADING SCHOOL_PT2_Q1 */}
        <div className='QuestionsSubSection'>
          {/* 1. Name of Vedvidhyalaya and full address */}
          <Row>
            <Col
              xs={24}
              lg={12}
              xl={8}
            >
              <Form.Item
                name='SCHOOL_PT2_Q1_NAME'
                label={Text({
                  tid: "SCHOOL_PT2_Q1_NAME",
                  def: "Name of Vedvidhyalaya and full address",
                })}
                rules={inputRequiredBasicRule}
              >
                <Input
                  disabled={disabled}
                  onBlur={(e) =>
                    handleFieldChange("SCHOOL_PT2_Q1_NAME", e.target.value)
                  }
                />
              </Form.Item>
            </Col>
          </Row>

          <Row>
            <Col
              xs={24}
              lg={12}
              xl={12}
            >
              <Form.Item
                name='SCHOOL_PT2_Q1_ADDRESS'
                label={Text({
                  tid: "SCHOOL_PT2_Q1_ADDRESS",
                  def: "Full address of Vedvidhyalaya",
                })}
                rules={inputRequiredBasicRule}
              >
                <>
                  <Input.TextArea
                    disabled={disabled}
                    onBlur={(e) => {
                      handleFieldChange(
                        "SCHOOL_PT2_Q1_ADDRESS",
                        e.target.value
                      );
                    }}
                    onChange={(e) => {
                      form.setFieldValue(
                        "SCHOOL_PT2_Q1_ADDRESS",
                        e.target.value
                      );
                    }}
                    maxLength={INPUT_LENGTH.TEXTAREA_LENGTH}
                    style={{ paddingBottom: 20 }}
                  />
                </>
              </Form.Item>
              <LengthCounter
                form={form}
                fieldName='SCHOOL_PT2_Q1_ADDRESS'
              />
            </Col>
          </Row>
        </div>

        {/* Q2). HEADING SCHOOL_PT2_Q2 */}
        <Row>
          <Col xs={24}>
            <Typography.Text>
              {Text({
                tid: "SCHOOL_PT2_Q2",
                def: "",
              })}
            </Typography.Text>
          </Col>
        </Row>
        {/* Q2). TABLE SCHOOL_PT2_Q2 DUE */}
        <Row>
          <Col xs={24}>
            <Form.Item name='SCHOOL_PT2_Q2'>
              <Table
                columns={SCHOOL_PT2_Q2}
                dataSource={form.getFieldValue("SCHOOL_PT2_Q2") || []}
                pagination={false}
                style={{ width: "100%" }}
                rowKey={(record, index) =>
                  index !== undefined ? index.toString() : ""
                }
              />
            </Form.Item>
            {!disabled && (
              <Flex className='belowTableAddRemoveButton'>
                <Button
                  disabled={disabled}
                  onClick={() => {
                    const currentData =
                      form.getFieldValue("SCHOOL_PT2_Q2") || [];
                    const newRow = {
                      teacherName: "",
                      vedaShakha: "",
                      vedicStudyLevel: "",
                      age: "",
                      fullTimeOrPartTime: "",
                      SCHOOL_PT2_Q2_DOC: "",
                    };
                    form.setFieldsValue({
                      SCHOOL_PT2_Q2: [...currentData, newRow],
                    });
                    setRowCount((prev) => ({
                      ...prev,
                      SCHOOL_PT2_Q2: prev.SCHOOL_PT2_Q2 + 1,
                    }));
                  }}
                >
                  {Text({ tid: "ADD", def: "Add" })}
                </Button>
              </Flex>
            )}
          </Col>
        </Row>

        {/* Q3). HEADING SCHOOL_PT2_Q3 */}
        <Row>
          <Col xs={24}>
            <Typography.Text>
              {Text({
                tid: "SCHOOL_PT2_Q3",
                def: "",
              })}
            </Typography.Text>
          </Col>
        </Row>
        {/* Q3). TABLE SCHOOL_PT2_Q3 DUE */}
        <Row>
          <Col xs={24}>
            <Form.Item name='SCHOOL_PT2_Q3'>
              <Table
                columns={SCHOOL_PT2_Q3}
                dataSource={form.getFieldValue("SCHOOL_PT2_Q3") || []}
                pagination={false}
                style={{ width: "100%" }}
                rowKey={(record, index) =>
                  index !== undefined ? index.toString() : ""
                }
              />
            </Form.Item>
            {!disabled && (
              <Flex className='belowTableAddRemoveButton'>
                <Button
                  disabled={disabled}
                  onClick={() => {
                    const currentData =
                      form.getFieldValue("SCHOOL_PT2_Q3") || [];
                    const newRow = {
                      teacherName: "",
                      vedaShakha: "",
                      startingOfTeaching: "",
                      endingOfTeaching: "",
                      reasonOfLeaving: "",
                    };
                    form.setFieldsValue({
                      SCHOOL_PT2_Q3: [...currentData, newRow],
                    });
                    setRowCount((prev) => ({
                      ...prev,
                      SCHOOL_PT2_Q3: prev.SCHOOL_PT2_Q3 + 1,
                    }));
                  }}
                >
                  {Text({ tid: "ADD", def: "Add" })}
                </Button>
              </Flex>
            )}
          </Col>
        </Row>

        {/* Q4). HEADING SCHOOL_PT2_Q4 */}
        <Row>
          <Col xs={24}>
            <Typography.Text>
              {Text({
                tid: "SCHOOL_PT2_Q4",
                def: "",
              })}
            </Typography.Text>
          </Col>
        </Row>
        {/* Q4). TABLE SCHOOL_PT2_Q4 DUE */}
        <Row>
          <Col xs={24}>
            <Form.Item name='SCHOOL_PT2_Q4'>
              <Table
                columns={SCHOOL_PT2_Q4}
                dataSource={form.getFieldValue("SCHOOL_PT2_Q4") || []}
                pagination={false}
                style={{ width: "100%" }}
                rowKey={(record, index) =>
                  index !== undefined ? index.toString() : ""
                }
              />
            </Form.Item>
            {!disabled && (
              <Flex className='belowTableAddRemoveButton'>
                <Button
                  disabled={disabled}
                  onClick={() => {
                    const currentData =
                      form.getFieldValue("SCHOOL_PT2_Q4") || [];
                    const newRow = {
                      teacherName: "",
                      awardName: "",
                      instituteName: "",
                      year: "",
                    };
                    form.setFieldsValue({
                      SCHOOL_PT2_Q4: [...currentData, newRow],
                    });
                    setRowCount((prev) => ({
                      ...prev,
                      SCHOOL_PT2_Q4: prev.SCHOOL_PT2_Q4 + 1,
                    }));
                  }}
                >
                  {Text({ tid: "ADD", def: "Add" })}
                </Button>
              </Flex>
            )}
          </Col>
        </Row>

        {/* Q5). HEADING SCHOOL_PT2_Q5 */}
        <Row>
          <Col xs={24}>
            <Typography.Text>
              {Text({
                tid: "SCHOOL_PT2_Q5",
                def: "",
              })}
            </Typography.Text>
          </Col>
        </Row>
        {/* Q5). TABLE SCHOOL_PT2_Q5 DUE */}
        <Row>
          <Col xs={24}>
            <Form.Item name='SCHOOL_PT2_Q5'>
              <Table
                columns={SCHOOL_PT2_Q5}
                dataSource={form.getFieldValue("SCHOOL_PT2_Q5") || []}
                pagination={false}
                style={{ width: "100%" }}
                rowKey={(record, index) =>
                  index !== undefined ? index.toString() : ""
                }
              />
            </Form.Item>
            {!disabled && (
              <Flex className='belowTableAddRemoveButton'>
                <Button
                  disabled={disabled}
                  onClick={() => {
                    const currentData =
                      form.getFieldValue("SCHOOL_PT2_Q5") || [];
                    const newRow = {
                      vedicStudentName: "",
                      age: "",
                      vedaBranch: "",
                      studyLevel: "",
                    };
                    form.setFieldsValue({
                      SCHOOL_PT2_Q5: [...currentData, newRow],
                    });
                    setRowCount((prev) => ({
                      ...prev,
                      SCHOOL_PT2_Q5: prev.SCHOOL_PT2_Q5 + 1,
                    }));
                  }}
                >
                  {Text({ tid: "ADD", def: "Add" })}
                </Button>
              </Flex>
            )}
          </Col>
        </Row>

        {/* Q6). HEADING SCHOOL_PT2_Q6 */}
        <Row>
          <Col xs={24}>
            <Typography.Text>
              {Text({
                tid: "SCHOOL_PT2_Q6",
                def: "",
              })}
            </Typography.Text>
          </Col>
        </Row>
        {/* Q6). TABLE SCHOOL_PT2_Q6 DUE */}
        <Row>
          <Col xs={24}>
            <Form.Item name='SCHOOL_PT2_Q6'>
              <Table
                columns={SCHOOL_PT2_Q6}
                dataSource={form.getFieldValue("SCHOOL_PT2_Q6") || []}
                pagination={false}
                style={{ width: "100%" }}
                rowKey={(record, index) =>
                  index !== undefined ? index.toString() : ""
                }
              />
            </Form.Item>
            {!disabled && (
              <Flex className='belowTableAddRemoveButton'>
                <Button
                  disabled={disabled}
                  onClick={() => {
                    const currentData =
                      form.getFieldValue("SCHOOL_PT2_Q6") || [];
                    const newRow = {
                      vedicStudentName: "",
                      vedicStudyLevel: "",
                      examinationInstitutionName: "",
                      year: "",
                      category: "",
                    };
                    form.setFieldsValue({
                      SCHOOL_PT2_Q6: [...currentData, newRow],
                    });
                    setRowCount((prev) => ({
                      ...prev,
                      SCHOOL_PT2_Q6: prev.SCHOOL_PT2_Q6 + 1,
                    }));
                  }}
                >
                  {Text({ tid: "ADD", def: "Add" })}
                </Button>
              </Flex>
            )}
          </Col>
        </Row>

        {/* Q7). HEADING SCHOOL_PT2_Q7 */}
        <Row>
          <Col xs={24}>
            <Typography.Text>
              {Text({
                tid: "SCHOOL_PT2_Q7",
                def: "",
              })}
            </Typography.Text>
          </Col>
        </Row>
        {/* Q7). TABLE SCHOOL_PT2_Q7 DUE */}
        <Row>
          <Col xs={24}>
            <Form.Item name='SCHOOL_PT2_Q7'>
              <Table
                columns={SCHOOL_PT2_Q7}
                dataSource={form.getFieldValue("SCHOOL_PT2_Q7") || []}
                pagination={false}
                style={{ width: "100%" }}
                rowKey={(record, index) =>
                  index !== undefined ? index.toString() : ""
                }
              />
            </Form.Item>
            {!disabled && (
              <Flex className='belowTableAddRemoveButton'>
                <Button
                  disabled={disabled}
                  onClick={() => {
                    const currentData =
                      form.getFieldValue("SCHOOL_PT2_Q7") || [];
                    const newRow = {
                      vedicStudentName: "",
                      awardName: "",
                      institutionName: "",
                      year: "",
                    };
                    form.setFieldsValue({
                      SCHOOL_PT2_Q7: [...currentData, newRow],
                    });
                    setRowCount((prev) => ({
                      ...prev,
                      SCHOOL_PT2_Q7: prev.SCHOOL_PT2_Q7 + 1,
                    }));
                  }}
                >
                  {Text({ tid: "ADD", def: "Add" })}
                </Button>
              </Flex>
            )}
          </Col>
        </Row>

        {/* Q8). HEADING SCHOOL_PT2_Q8 */}
        <Row>
          <Col xs={24}>
            <Typography.Text>
              {Text({
                tid: "SCHOOL_PT2_Q8",
                def: "",
              })}
            </Typography.Text>
          </Col>
        </Row>
        {/* Q8). TABLE SCHOOL_PT2_Q8 DUE */}
        <Row>
          <Col xs={24}>
            <Form.Item name='SCHOOL_PT2_Q8'>
              <Table
                columns={SCHOOL_PT2_Q8}
                dataSource={form.getFieldValue("SCHOOL_PT2_Q8") || []}
                pagination={false}
                style={{ width: "100%" }}
                rowKey={(record, index) =>
                  index !== undefined ? index.toString() : ""
                }
              />
            </Form.Item>
            {!disabled && (
              <Flex className='belowTableAddRemoveButton'>
                <Button
                  disabled={disabled}
                  onClick={() => {
                    const currentData =
                      form.getFieldValue("SCHOOL_PT2_Q8") || [];
                    const newRow = {
                      formarStudentName: "",
                      year: "",
                      vedicBranch: "",
                      currentInstitutionOfVedicTeaching: "",
                    };
                    form.setFieldsValue({
                      SCHOOL_PT2_Q8: [...currentData, newRow],
                    });
                    setRowCount((prev) => ({
                      ...prev,
                      SCHOOL_PT2_Q8: prev.SCHOOL_PT2_Q8 + 1,
                    }));
                  }}
                >
                  {Text({ tid: "ADD", def: "Add" })}
                </Button>
              </Flex>
            )}
          </Col>
        </Row>

        {/* Q9). HEADING SCHOOL_PT2_Q9 */}
        <Row>
          <Col xs={24}>
            <Typography.Text>
              {Text({
                tid: "SCHOOL_PT2_Q9",
                def: "",
              })}
            </Typography.Text>
          </Col>
        </Row>
        {/* Q9). TABLE SCHOOL_PT2_Q9 DUE */}
        <Row className='mb-20'>
          <Col xs={24}>
            <Form.Item name='SCHOOL_PT2_Q9'>
              <Table
                columns={SCHOOL_PT2_Q9}
                dataSource={form.getFieldValue("SCHOOL_PT2_Q9") || []}
                pagination={false}
                style={{ width: "100%" }}
                rowKey={(record, index) =>
                  index !== undefined ? index.toString() : ""
                }
              />
            </Form.Item>
            {!disabled && (
              <Flex className='belowTableAddRemoveButton'>
                <Button
                  disabled={disabled}
                  onClick={() => {
                    const currentData =
                      form.getFieldValue("SCHOOL_PT2_Q9") || [];
                    const newRow = {
                      awardHonorName: "",
                      institutionName: "",
                      year: "",
                      SCHOOL_PT2_Q9_DOC: "",
                    };
                    form.setFieldsValue({
                      SCHOOL_PT2_Q9: [...currentData, newRow],
                    });
                    setRowCount((prev) => ({
                      ...prev,
                      SCHOOL_PT2_Q9: prev.SCHOOL_PT2_Q9 + 1,
                    }));
                  }}
                >
                  {Text({ tid: "ADD", def: "Add" })}
                </Button>
              </Flex>
            )}
          </Col>
        </Row>

        {/* Q10). AUTO-FILLED UPLOADED DOCUMENTS TABLE */}
        <Row>
          <Col xs={24}>
            <Typography.Text>
              {Text({
                tid: "SCHOOL_PT2_Q16",
                def: "Details of documents attached with this application:",
              })}
            </Typography.Text>
            <Typography.Text>
              {Text({
                tid: "SCHOOL_PT2_Q16_A",
                def: "Details of documents attached with this application:",
              })}
            </Typography.Text>
          </Col>
        </Row>
        <Row className='mb-20'>
          <Col xs={24}>
            <Form.Item name='SCHOOL_PT2_Q16'>
              <Table
                columns={TABLE_COLUMNS_SCHOOL_PT2_Q16}
                dataSource={form.getFieldValue("SCHOOL_PT2_Q16") || []}
                pagination={false}
                style={{ width: "100%" }}
                rowKey={(record, index) =>
                  index !== undefined ? index.toString() : ""
                }
              />
            </Form.Item>
          </Col>
        </Row>

        {/* INSTITUTION HEAD AFFIDAVIT TITLE */}
        <div
          id='school_form2_ceo_affidavit'
          className='affidavitSection'
        >
          <Row>
            <Col>
              <Typography.Text>
                {Text({
                  tid: "INSTITUTION_HEAD_AFFIDAVIT_TITLE",
                  def: "",
                })}
              </Typography.Text>
            </Col>
          </Row>
          {/* INSTITUTION HEAD AFFIDAVIT INTRO */}
          <Row className='affidavitRow'>
            <Col xs={24}>
              <Typography.Text
                type='secondary'
                style={{
                  maxWidth: "max-content",
                  marginBottom: "0 !important",
                }}
              >
                {Text({
                  tid: "INSTITUTION_HEAD_AFFIDAVIT_INTRO_1",
                  def: "INSTITUTION_HEAD_AFFIDAVIT_INTRO_1",
                })}
              </Typography.Text>
              <Form.Item
                name='SCHOOL_PT2_Q10'
                rules={[
                  {
                    required: true,
                    message: Text({ tid: "REQUIRED", def: "Required" }),
                  },
                ]}
                className='affidavitRowInput'
              >
                <Input
                  disabled={disabled}
                  onBlur={(e) =>
                    handleFieldChange("SCHOOL_PT2_Q10", e.target.value)
                  }
                  id={applyPdfStyles ? "affidavit_input" : ""}
                />
              </Form.Item>
              <Typography.Text
                type='secondary'
                style={{ maxWidth: "max-content", marginBottom: "0" }}
              >
                {Text({
                  tid: "INSTITUTION_HEAD_AFFIDAVIT_INTRO_2",
                  def: "INSTITUTION_HEAD_AFFIDAVIT_INTRO_2",
                })}
              </Typography.Text>
            </Col>
          </Row>

          {/* INSTITUTION HEAD AFFIDAVIT DESCRIPTION */}
          <ul>
            <li>
              <Row className='affidavitRow'>
                <Col xs={24}>
                  <Typography.Text
                    type='secondary'
                    style={{ maxWidth: "max-content", marginBottom: "0" }}
                  >
                    {Text({
                      tid: "INSTITUTION_HEAD_AFFIDAVIT_CLAUSE_11",
                      def: "INSTITUTION_HEAD_AFFIDAVIT_CLAUSE_11",
                    })}
                  </Typography.Text>
                  <Form.Item
                    name='SCHOOL_PT2_Q11'
                    rules={numberInputRules({
                      required: true,
                      allowNegative: false,
                      min: 0,
                      max: 120,
                    })}
                    className='affidavitRowInput'
                  >
                    <Input
                      disabled={disabled}
                      onBlur={(e) =>
                        handleFieldChange("SCHOOL_PT2_Q11", e.target.value)
                      }
                      id={applyPdfStyles ? "affidavit_input" : ""}
                    />
                  </Form.Item>
                  <Typography.Text
                    type='secondary'
                    style={{ maxWidth: "max-content", marginBottom: "0" }}
                  >
                    {Text({
                      tid: "INSTITUTION_HEAD_AFFIDAVIT_CLAUSE_12",
                      def: "INSTITUTION_HEAD_AFFIDAVIT_CLAUSE_12",
                    })}
                  </Typography.Text>
                </Col>
              </Row>
            </li>
            <Row>
              <Col>
                <li>
                  <Typography.Text type='secondary'>
                    {Text({
                      tid: "INSTITUTION_HEAD_AFFIDAVIT_CLAUSE_2",
                      def: "INSTITUTION_HEAD_AFFIDAVIT_CLAUSE_2",
                    })}
                  </Typography.Text>
                </li>
                <li>
                  <Typography.Text type='secondary'>
                    {Text({
                      tid: "INSTITUTION_HEAD_AFFIDAVIT_CLAUSE_3",
                      def: "INSTITUTION_HEAD_AFFIDAVIT_CLAUSE_3",
                    })}
                  </Typography.Text>
                </li>
              </Col>
            </Row>
          </ul>
          <Row>
            <Col>
              <div className='mb-20'>
                <Typography.Text type='secondary'>
                  {Text({
                    tid: "INSTITUTION_HEAD_AFFIDAVIT_VERIFICATION",
                    def: "INSTITUTION_HEAD_AFFIDAVIT_VERIFICATION",
                  })}
                </Typography.Text>
                <Typography.Text type='secondary'>
                  {Text({
                    tid: "INSTITUTION_HEAD_AFFIDAVIT_ACCEPTANCE",
                    def: "INSTITUTION_HEAD_AFFIDAVIT_ACCEPTANCE",
                  })}
                </Typography.Text>
              </div>
            </Col>
          </Row>
          {/* NAME */}
          <Row className='QuestionsSubSection affidavitQuestionFirst'>
            <Col
              xs={24}
              lg={12}
              xl={8}
            >
              {applyPdfStyles ? (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    minWidth: "max-content",
                  }}
                >
                  <span style={{ minWidth: "max-content" }}>
                    {Text({
                      tid: "INSTITUTION_HEAD_FULL_NAME",
                      def: "INSTITUTION_HEAD_FULL_NAME",
                    })}
                  </span>
                  <Form.Item
                    name='SCHOOL_PT2_Q13'
                    style={{ marginBottom: 0, flex: 1 }}
                  >
                    <Input
                      disabled={false}
                      onBlur={(e) =>
                        handleFieldChange("SCHOOL_PT2_Q13", e.target.value)
                      }
                      id={applyPdfStyles ? "affidavit_input" : ""}
                    />
                  </Form.Item>
                </div>
              ) : (
                <Form.Item
                  name='SCHOOL_PT2_Q13'
                  label={Text({
                    tid: "INSTITUTION_HEAD_FULL_NAME",
                    def: "INSTITUTION_HEAD_FULL_NAME",
                  })}
                >
                  <Input
                    disabled={disabled}
                    onBlur={(e) =>
                      handleFieldChange("SCHOOL_PT2_Q13", e.target.value)
                    }
                    id={applyPdfStyles ? "affidavit_input" : ""}
                  />
                </Form.Item>
              )}
            </Col>
          </Row>

          {/* LOCATION */}
          <Row className='QuestionsSubSection'>
            <Col
              xs={24}
              lg={12}
              xl={8}
            >
              {applyPdfStyles ? (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    minWidth: "max-content",
                  }}
                >
                  <span style={{ minWidth: "max-content" }}>
                    {Text({
                      tid: "INSTITUTION_HEAD_LOCATION",
                      def: "INSTITUTION_HEAD_LOCATION",
                    })}
                  </span>
                  <Form.Item
                    name='SCHOOL_PT2_Q14'
                    style={{ marginBottom: 0, flex: 1 }}
                  >
                    <Input
                      disabled={false}
                      onBlur={(e) =>
                        handleFieldChange("SCHOOL_PT2_Q14", e.target.value)
                      }
                      id={applyPdfStyles ? "affidavit_input" : ""}
                    />
                  </Form.Item>
                </div>
              ) : (
                <Form.Item
                  name='SCHOOL_PT2_Q14'
                  label={Text({
                    tid: "INSTITUTION_HEAD_LOCATION",
                    def: "INSTITUTION_HEAD_LOCATION",
                  })}
                >
                  <Input
                    disabled={disabled}
                    onBlur={(e) =>
                      handleFieldChange("SCHOOL_PT2_Q14", e.target.value)
                    }
                    id={applyPdfStyles ? "affidavit_input" : ""}
                  />
                </Form.Item>
              )}
            </Col>
          </Row>

          {/* DATE */}
          <Row className='QuestionsSubSection'>
            <Col
              xs={24}
              lg={12}
              xl={8}
            >
              {applyPdfStyles ? (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    minWidth: "max-content",
                  }}
                >
                  <span style={{ minWidth: "max-content" }}>
                    {Text({
                      tid: "INSTITUTION_HEAD_DATE",
                      def: "INSTITUTION_HEAD_DATE",
                    })}
                  </span>
                  <Form.Item
                    name='SCHOOL_PT2_Q15'
                    style={{ marginBottom: 0, flex: 1 }}
                  >
                    <Input
                      disabled={false}
                      type={undefined}
                      onChange={(e) =>
                        handleFieldChange("SCHOOL_PT2_Q15", e.target.value)
                      }
                      id={applyPdfStyles ? "affidavit_input" : ""}
                    />
                  </Form.Item>
                </div>
              ) : (
                <Form.Item
                  name='SCHOOL_PT2_Q15'
                  label={Text({
                    tid: "INSTITUTION_HEAD_DATE",
                    def: "INSTITUTION_HEAD_DATE",
                  })}
                >
                  <Input
                    disabled={disabled}
                    type={applyPdfStyles ? undefined : "date"}
                    max={
                      applyPdfStyles
                        ? undefined
                        : disableFutureDates_on_input_date_max()
                    }
                    onChange={(e) =>
                      handleFieldChange("SCHOOL_PT2_Q15", e.target.value)
                    }
                    id={applyPdfStyles ? "affidavit_input" : ""}
                  />
                </Form.Item>
              )}
            </Col>
          </Row>

          {applyPdfStyles ? (
            <Row className='QuestionsSubSection'>
              <Col
                xs={24}
                lg={12}
                xl={8}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    minWidth: "max-content",
                  }}
                >
                  <span style={{ minWidth: "max-content" }}>
                    {Text({
                      tid: "INSTITUTION_HEAD_SIGNATURE",
                      def: "Signature of the Head of the Institution",
                    })}
                  </span>
                  <Form.Item style={{ marginBottom: 0, flex: 1 }}>
                    <Input id={applyPdfStyles ? "affidavit_input" : ""} />
                  </Form.Item>
                </div>
              </Col>
            </Row>
          ) : null}
        </div>
        <div className='affidavitSection'>
          {!disabled && (
            <Row className='mb-15 QuestionsSubSection'>
              <Button
                className='downloadAffidavitButton'
                type='default'
                onClick={() =>
                  handleGeneratePDF(
                    "school_form2_ceo_affidavit",
                    "school_form2_ceo_affidavit"
                  )
                }
              >
                {Text({
                  tid: "DOWNLOAD_AFFIDAVIT",
                  def: "Download affidavit",
                })}
              </Button>
            </Row>
          )}

          {/* INSTITUTION HEAD SIGNATURE */}
          <Row className='QuestionsSubSection'>
            <Col
              xs={24}
              lg={12}
              xl={8}
            >
              <Form.Item
                name='SCHOOL_PT2_Q12'
                label={Text({
                  tid: "UPLOAD_AFFIDAVIT",
                  def: "UPLOAD affidavit",
                })}
                rules={[
                  {
                    required:
                      guruSignature ||
                      (documentsData?.length &&
                        documentsData.find(
                          (doc: any) => doc.labelName === "SCHOOL_PT2_Q12"
                        ))
                        ? false
                        : true,
                    message: Text({ tid: "REQUIRED", def: "Required" }),
                  },
                ]}
              >
                <Input
                  disabled={disabled}
                  type='file'
                  accept='.jpg,.jpeg,.png,.pdf'
                  // onChange={(e) => {
                  //   if (e.target.files && e.target.files[0]) {
                  //     uploadDocument(
                  //       e.target.files[0],
                  //       eDocType.OTHER,
                  //       "SCHOOL_PT2_Q12"
                  //     );
                  //   }
                  // }}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!validateFile(file as File).valid) {
                      onShowToast({
                        type: ToastType.error,
                        title: <CloseOutlined />,
                        position: ToastOpen.leftBottom,
                        content: validateFile(file as File).error,
                      });
                      e.target.value = "";
                      form.resetFields(["SCHOOL_PT2_Q12"]);
                    } else if (file) {
                      uploadDocument(file, eDocType.OTHER, "SCHOOL_PT2_Q12");
                    }
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={24}>
              {" "}
              {guruSignature ? (
                <div className='previewLink'>
                  <span>
                    {Text({
                      tid: "ATTACHED_DOCUMENT",
                      def: "Attached Document",
                    })}
                    :
                  </span>
                  <a
                    href={`${process.env.REACT_APP_PUBLIC_API_MEDIA_BASEURL}${guruSignature}`}
                    target='_blank'
                    rel='noreferrer'
                    className='primary-link'
                  >
                    {PREVIEW}
                  </a>
                </div>
              ) : (
                documentsData &&
                documentsData.length > 0 &&
                documentsData
                  .filter((doc: any) => doc.labelName === "SCHOOL_PT2_Q12")
                  .map((doc: any) => (
                    <div className='previewLink'>
                      <span>
                        {Text({
                          tid: "ATTACHED_DOCUMENT",
                          def: "Attached Document",
                        })}
                        :
                      </span>
                      <a
                        href={`${process.env.REACT_APP_PUBLIC_API_MEDIA_BASEURL}${doc.docPath}`}
                        target='_blank'
                        rel='noreferrer'
                        className='primary-link'
                      >
                        {doc.docName}
                      </a>
                    </div>
                  ))
              )}
            </Col>
          </Row>
        </div>

        {/* -------------------------------------------------------------- */}
        {/* SUBMIT BUTTON */}
        <Row
          style={
            disabled === true
              ? { marginTop: "var(--bs-item-negative-margin-30)" }
              : undefined
          }
        >
          {disabled === false && (
            <Button
              block
              type='primary'
              // variant="filled"
              htmlType='submit'
              disabled={isLoading || isSubmitting || isUploading}
              className='formSubmitButton'
              loading={isLoading || isSubmitting}
            >
              {Text({ tid: "SUBMIT", def: "SUBMIT" })}
            </Button>
          )}
        </Row>
      </Form>
    </div>
  );
};

export default SchoolPartTwo;
